import { Routes, Route } from 'react-router-dom'
import { AppShell } from '@mantine/core'
import { useHotkeys } from 'react-hotkeys-hook'
import { Suspense, lazy } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { Header } from './components/layout/Header'
import { Footer } from './components/layout/Footer'
import { DynamicHead } from './components/DynamicHead'
import { TestDomainIndicator } from './components/TestDomainIndicator'
import { SpotlightProvider } from './components/SpotlightProvider'
import { ProtectedRoute } from './components/ProtectedRoute'
import { AdminRoute } from './components/AdminRoute'
import { AdminPageWrapper } from './components/AdminPageWrapper'
import { CountryRoute } from './components/CountryRoute'
import { CountryGameRoute } from './components/CountryGameRoute'
import { CountryPlatformRoute } from './components/CountryPlatformRoute'
import { CookieWall } from './components/CookieWall'
import { PageLoader, AdminPageLoader } from './components/PageLoader'
import { ScrollToTop } from './components/ScrollToTop'

// User Pages - Keep these as regular imports for faster initial load
import { HomePage } from './pages/HomePage'
import { GameDetailPage } from './pages/GameDetailPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { BrandingTestPage } from './pages/BrandingTestPage'

// User Pages - Lazy load these to reduce initial bundle size
const PlatformPage = lazy(() => import('./pages/PlatformPage').then(module => ({ default: module.PlatformPage })))
const GamesPage = lazy(() => import('./pages/GamesPage').then(module => ({ default: module.GamesPage })))
const WishlistPage = lazy(() => import('./pages/WishlistPage').then(module => ({ default: module.WishlistPage })))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(module => ({ default: module.NotificationsPage })))
const LoginPage = lazy(() => import('./pages/LoginPage').then(module => ({ default: module.LoginPage })))
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(module => ({ default: module.RegisterPage })))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage').then(module => ({ default: module.VerifyEmailPage })))
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage').then(module => ({ default: module.UserSettingsPage })))
const StoresPage = lazy(() => import('./pages/StoresPage').then(module => ({ default: module.StoresPage })))
const StoreRatingsPage = lazy(() => import('./pages/StoreRatingsPage').then(module => ({ default: module.StoreRatingsPage })))
const FAQPage = lazy(() => import('./pages/FAQPage').then(module => ({ default: module.FAQPage })))
const ContactPage = lazy(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })))

// Admin Pages - Lazy load these to reduce initial bundle size
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(module => ({ default: module.AdminDashboard })))
const AdminGames = lazy(() => import('./pages/admin/AdminGames').then(module => ({ default: module.AdminGames })))
const AdminGameDetail = lazy(() => import('./pages/admin/AdminGameDetail').then(module => ({ default: module.AdminGameDetail })))
const AdminGameEdit = lazy(() => import('./pages/admin/AdminGameEdit').then(module => ({ default: module.AdminGameEdit })))
const AdminGamePlatforms = lazy(() => import('./pages/admin/AdminGamePlatforms').then(module => ({ default: module.AdminGamePlatforms })))
const AdminGamePlatformImages = lazy(() => import('./pages/admin/AdminGamePlatformImages').then(module => ({ default: module.AdminGamePlatformImages })))
const AdminGamePlatformLandscapeImages = lazy(() => import('./pages/admin/AdminGamePlatformLandscapeImages').then(module => ({ default: module.AdminGamePlatformLandscapeImages })));
const AdminPlatforms = lazy(() => import('./pages/admin/AdminPlatforms').then(module => ({ default: module.AdminPlatforms })))
const AdminStores = lazy(() => import('./pages/admin/AdminStores').then(module => ({ default: module.AdminStores })))
const AdminStoreDetail = lazy(() => import('./pages/admin/AdminStoreDetail').then(module => ({ default: module.AdminStoreDetail })))
const AdminPrices = lazy(() => import('./pages/admin/AdminPrices').then(module => ({ default: module.AdminPrices })))
const AdminPriceDetail = lazy(() => import('./pages/admin/AdminPriceDetail').then(module => ({ default: module.AdminPriceDetail })))
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics').then(module => ({ default: module.AdminAnalytics })))
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates').then(module => ({ default: module.default })))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers').then(module => ({ default: module.AdminUsers })))
const AdminComments = lazy(() => import('./pages/admin/AdminComments').then(module => ({ default: module.AdminComments })))
const AdminCensoredWords = lazy(() => import('./pages/admin/AdminCensoredWords').then(module => ({ default: module.AdminCensoredWords })))
const AdminCommentSettings = lazy(() => import('./pages/admin/AdminCommentSettings').then(module => ({ default: module.AdminCommentSettings })))
const AdminFAQs = lazy(() => import('./pages/admin/AdminFAQs').then(module => ({ default: module.AdminFAQs })))
const AdminSales = lazy(() => import('./pages/admin/AdminSales').then(module => ({ default: module.AdminSales })));

import { useSpotlight } from '@/hooks/useSpotlight'
import { useDarkMode } from './hooks/useDarkMode'

// Error fallback for admin components
const AdminErrorFallback = ({ error }: { error: Error }) => (
    <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '1.2rem',
        color: 'var(--mantine-color-red-6)',
        padding: '2rem',
        textAlign: 'center'
    }}>
        <h3>Error loading admin interface</h3>
        <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
            {error.message}
        </p>
        <button
            onClick={() => window.location.reload()}
            style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--mantine-color-blue-6)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
            }}
        >
            Reload Page
        </button>
    </div>
)

function AppContent() {
    const { open: openSpotlight } = useSpotlight()
    const { colorScheme } = useDarkMode()

    useHotkeys('mod+k', () => {
        openSpotlight()
    })

    return (
        <>
            <DynamicHead />
            <TestDomainIndicator />
            <ScrollToTop />
            <AppShell
                header={{ height: { base: 40, sm: 140 } }}
                footer={{ height: 60 }}
                style={{
                    backgroundColor: colorScheme === 'light'
                        ? 'rgba(255, 255, 255, 1)'
                        : 'transparent'
                }}
            >
                <AppShell.Header>
                    <Header />
                </AppShell.Header>

                <AppShell.Main>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/games" element={
                            <Suspense fallback={<PageLoader />}>
                                <GamesPage />
                            </Suspense>
                        } />
                        <Route path="/games/:slug" element={<GameDetailPage />} />
                        <Route path="/games/:slug/platforms/:platformSlug" element={<GameDetailPage />} />
                        <Route path="/game/:slug/:platformSlug" element={<GameDetailPage />} />
                        <Route path="/platform/:slug" element={
                            <Suspense fallback={<PageLoader />}>
                                <PlatformPage />
                            </Suspense>
                        } />
                        <Route path="/login" element={
                            <Suspense fallback={<PageLoader />}>
                                <LoginPage />
                            </Suspense>
                        } />
                        <Route path="/register" element={
                            <Suspense fallback={<PageLoader />}>
                                <RegisterPage />
                            </Suspense>
                        } />
                        <Route path="/forgot-password" element={
                            <Suspense fallback={<PageLoader />}>
                                <ForgotPasswordPage />
                            </Suspense>
                        } />
                        <Route path="/reset-password" element={
                            <Suspense fallback={<PageLoader />}>
                                <ResetPasswordPage />
                            </Suspense>
                        } />
                        <Route path="/verify-email" element={
                            <Suspense fallback={<PageLoader />}>
                                <VerifyEmailPage />
                            </Suspense>
                        } />

                        <Route path="/wishlist" element={
                            <ProtectedRoute>
                                <Suspense fallback={<PageLoader />}>
                                    <WishlistPage />
                                </Suspense>
                            </ProtectedRoute>
                        } />

                        <Route path="/notifications" element={
                            <ProtectedRoute>
                                <Suspense fallback={<PageLoader />}>
                                    <NotificationsPage />
                                </Suspense>
                            </ProtectedRoute>
                        } />

                        <Route path="/settings" element={
                            <ProtectedRoute>
                                <Suspense fallback={<PageLoader />}>
                                    <UserSettingsPage />
                                </Suspense>
                            </ProtectedRoute>
                        } />

                        <Route path="/stores" element={
                            <Suspense fallback={<PageLoader />}>
                                <StoresPage />
                            </Suspense>
                        } />
                        <Route path="/stores/:storeId/ratings" element={
                            <Suspense fallback={<PageLoader />}>
                                <StoreRatingsPage />
                            </Suspense>
                        } />
                        <Route path="/faq" element={
                            <Suspense fallback={<PageLoader />}>
                                <FAQPage />
                            </Suspense>
                        } />
                        <Route path="/contact" element={
                            <Suspense fallback={<PageLoader />}>
                                <ContactPage />
                            </Suspense>
                        } />

                        {/* Admin routes with lazy loading and error boundaries */}
                        <Route path="/admin" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminDashboard />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/games" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminGames />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/games/:id" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminGameDetail />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/games/:id/edit" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminGameEdit />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/game-platforms" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminGamePlatforms />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/game-platform-images" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminGamePlatformImages />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/game-platform-images/landscape" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminGamePlatformLandscapeImages />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/platforms" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminPlatforms />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/stores" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminStores />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/stores/:id" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminStoreDetail />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/prices" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminPrices />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/prices/:gameId/:platformId" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminPriceDetail />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/analytics" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminAnalytics />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/email-templates" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminEmailTemplates />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/users" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminUsers />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/comments" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminComments />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/censored-words" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminCensoredWords />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/comment-settings" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminCommentSettings />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/faqs" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminFAQs />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />
                        <Route path="/admin/sales" element={
                            <AdminRoute>
                                <AdminPageWrapper>
                                    <ErrorBoundary FallbackComponent={AdminErrorFallback}>
                                        <Suspense fallback={<AdminPageLoader />}>
                                            <AdminSales />
                                        </Suspense>
                                    </ErrorBoundary>
                                </AdminPageWrapper>
                            </AdminRoute>
                        } />

                        {/* Country-specific game routes - these switch country context and show game details */}
                        <Route path="/be/game/:slug/:platformSlug" element={<CountryGameRoute />} />
                        <Route path="/nl/game/:slug/:platformSlug" element={<CountryGameRoute />} />
                        <Route path="/us/game/:slug/:platformSlug" element={<CountryGameRoute />} />
                        <Route path="/:countryCode([a-zA-Z]{2})/game/:slug/:platformSlug" element={<CountryGameRoute />} />

                        {/* Country-specific platform routes - these switch country context and show platform details */}
                        <Route path="/be/platform/:slug" element={<CountryPlatformRoute />} />
                        <Route path="/nl/platform/:slug" element={<CountryPlatformRoute />} />
                        <Route path="/us/platform/:slug" element={<CountryPlatformRoute />} />
                        <Route path="/:countryCode([a-zA-Z]{2})/platform/:slug" element={<CountryPlatformRoute />} />

                        {/* Country switching route - match 2-letter country codes (case insensitive) */}
                        <Route path="/be" element={<CountryRoute />} />
                        <Route path="/nl" element={<CountryRoute />} />
                        <Route path="/us" element={<CountryRoute />} />
                        <Route path="/:countryCode([a-zA-Z]{2})" element={<CountryRoute />} />

                        <Route path="/branding-test" element={<BrandingTestPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </AppShell.Main>

                <AppShell.Footer>
                    <Footer />
                </AppShell.Footer>

                <CookieWall />
            </AppShell>
        </>
    )
}

function App() {
    return (
        <SpotlightProvider>
            <AppContent />
        </SpotlightProvider>
    )
}

export default App